import React, { useState, useEffect, Fragment } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Loader from 'components/Loader';
import { createStructuredSelector } from 'reselect';
import { get, sortBy } from 'lodash';
import { useIsVertPhone, useIsMobile } from 'utils/hooks';
import flatMap from 'lodash/flatMap';
import moment from 'moment';
import Calendar from 'components/Calendar';
import { Button, Container, Icon, Div, Span } from 'components/Ui';
import { badgeTypes } from 'components/Calendar/constants';
import DayWrapper from 'components/Calendar/DayWrapper';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import { letterCoffee, letterFilter, letterWater } from 'images/calendar';
import { apiCall } from 'utils/swaggerClient';
import { openFile } from 'utils/fileDownload';
import { dataLayerPush } from 'utils/tracking';
import { showSomethingWentWrong as showSomethingWentWrongAction } from 'components/Notifier/actions';
import {
  selectCalendarDates,
  selectSelectedServiceLocation,
  selectIsFetchingCalendarDates,
  selectServiceLocationIsFetching,
  selectIsFirstDelivery,
  selectIsSelectedServiceLocationExcluded,
} from 'containers/PrimoAccount/selectors';
import {
  selectIsHardLockout,
  selectIsEquipmentPickupLockout,
} from 'containers/PrimoProfile/selectors';
import HtmlData from 'components/Ui/HtmlData/index';
import {
  loadCalendarDates as loadCalendarDatesRequest,
  loadRecurringOrder as loadRecurringOrderRequest,
  loadDeliveryOrder as loadDeliveryOrderRequest,
  removeHold as removeHoldRequest,
} from '../actions';
import styles from './styles.scss';
import OrderEditor from '../OrderEditor';
import EditDeliveryOrderDrawer from './EditDeliveryOrderDrawer';
import ProcessingModal from './ProcessingModal';

const renderLegend = (months, calendarDates, shownMonths) => (
  <div className={styles.legend}>
    <div className={styles.legendItems}>
      <div className={styles.types}>
        <div>
          <DayWrapper type={badgeTypes.RECURRING} />
          <Span>Scheduled delivery</Span>
        </div>
        <div>
          <DayWrapper type={badgeTypes.HOLD} />
          <Span>Skipped delivery</Span>
        </div>
      </div>
      <div className={styles.badges}>
        <div>
          <img className={styles.badge} src={letterWater} alt="" />
          <Span>Water</Span>
        </div>
        <div>
          <img className={styles.badge} src={letterFilter} alt="" />
          <Span>Filtration</Span>
        </div>
        <div>
          <img className={styles.badge} src={letterCoffee} alt="" />
          <Span>Coffee</Span>
        </div>
      </div>
    </div>
    {months.length !== 0 && (
      <div className={styles.legendNotifier}>
        {months.slice(0, shownMonths).map((month) => {
          const monthNotes = get(calendarDates, [month.format('YYYY-MM')]);

          if (monthNotes) {
            return flatMap(Object.entries(monthNotes), ([day, note]) => {
              const noteText =
                note.note || 'Rescheduled delivery due to the holiday.';
              if (note.type === badgeTypes.RESCHEDULED) {
                return (
                  <div yearMonthKey={`${month.format('YYYY-MM')}-${day}`}>
                    <span>{month.format(`MMMM ${day} YYYY:`)}</span>
                    <Span>{noteText}</Span>
                  </div>
                );
              }

              return null;
            });
          }

          return null;
        })}
      </div>
    )}
  </div>
);

const getTypeText = (order) => {
  const type = order.skipDelivery ? 'skip' : order.type;
  switch (type) {
    case 'oneTime':
      return 'Delivery Scheduled';
    case 'recurring':
      return 'Delivery Scheduled';
    case 'skip':
      return 'Delivery Skipped';
    default:
      return null;
  }
};

const getStyle = (order, isVertPhone) => {
  const type = order.skipDelivery ? 'skip' : 'scheduled';
  let style = null;
  switch (type) {
    case 'scheduled':
      style = styles.scheduledDelivery;
      break;
    case 'skip':
      style = styles.skippedDelivery;
      break;
    default:
      return null;
  }
  return classnames(style, {
    [styles.roundVPhone]: isVertPhone,
  });
};

const sortedOrders = (calendarDates, isVertPhone) => {
  const orders = [];
  const calendarKeys = Object.keys(calendarDates);
  calendarKeys.forEach((yearMonthKey) => {
    const dates = calendarDates[yearMonthKey];
    const dateKeys = Object.keys(dates);
    dateKeys.forEach((dayKey) => {
      const order = dates[dayKey];
      const text = getTypeText(order);
      if (text) {
        const date = `${yearMonthKey}-${dayKey}`;
        const day = moment(date).format('MM/DD/YY');
        const style = getStyle(order, isVertPhone);
        orders.push({ ...order, date, day, text, style });
      }
    });
  });
  return sortBy(orders, (order) => moment(order.date).format('YYYYMMDD'));
};

const renderOrders = (calendarDates, isVertPhone, onEditDeliveryOrder) =>
  sortedOrders(calendarDates, isVertPhone).map((order) => (
    <div key={order.date} className={styles.orderContainer}>
      <div className={styles.order}>
        <div className={order.style} />
        <Div
          className={classnames(styles.text, {
            [styles.textVPhone]: isVertPhone,
          })}
        >
          {order.text}
          {isVertPhone && (
            <div
              className={classnames(styles.day, {
                [styles.dayVPhone]: isVertPhone,
              })}
            >
              {order.day}
            </div>
          )}
        </Div>
        {!isVertPhone && <div className={styles.day}>{order.day}</div>}
        <Button
          className={classnames(styles.viewEdit, {
            [styles.viewEditVPhone]: isVertPhone,
          })}
          text="View/Edit"
          onClick={() => onEditDeliveryOrder(order)}
        />
      </div>
    </div>
  ));

const FutureOrders = ({
  calendarDates,
  loadCalendarDates,
  serviceLocation,
  isFetching,
  isFetchingServiceLocation,
  showSomethingWentWrong,
  loadRecurringOrder,
  loadDeliveryOrder,
  isHardLockout,
  isFirstDelivery,
  isEquipmentPickupLockout,
  isSelectedServiceLocationExcluded,
  history,
  removeHold,
}) => {
  const isVertPhone = useIsVertPhone();
  const isMobile = useIsMobile();
  const [deliveryOrderToLoad, changeDeliveryOrderToLoad] = useState();
  const [months, changeMonths] = useState([]);
  const [shownMonths, changeShownMonths] = useState(3);
  const [pdfFetching, changePdfFetching] = useState(false);

  const onEditRecurringOrder = () => {
    loadRecurringOrder();
  };

  const onEditDeliveryOrder = (deliveryOrder) => {
    dataLayerPush(
      'SelfServe',
      {
        event: 'manage-upcoming-deliveries',
        date: deliveryOrder.date,
        type: deliveryOrder.text,
      },
      'dlS19',
      'PUSH_LOAD_RECURRING_ORDER_SUCCESS',
    );
    localStorage.removeItem('profilePage');
    localStorage.removeItem('nextDeliveryPage');
    loadDeliveryOrder(deliveryOrder);
  };

  const handleChangeMonths = (newMonths) => {
    changeMonths(newMonths);
  };

  const handleBrowseProducts = () => {
    history.push(
      `/account/products/list?serviceLocationId=${serviceLocation.id}`,
    );
  };

  const handleChangeRange = (range) => {
    changeShownMonths(range);
  };

  const handleRefresh = () => {
    loadDates(months);
  };

  const handelCancelEditSkipDelivery = (deliveryOrder) => {
    loadDates(months);
    changeDeliveryOrderToLoad(deliveryOrder);
  };

  const removeHoldSuccessCallback = (deliveryOrder) => {
    loadDates(months);
    changeDeliveryOrderToLoad(deliveryOrder);
  };

  const handleRemoveHold = (deliveryOrder) => {
    removeHold({ deliveryOrder, successCallback: removeHoldSuccessCallback });
  };

  const handlePdfChange = async () => {
    changePdfFetching(true);
    try {
      const { data } = await apiCall({
        operationId: 'ServiceLocations.getServiceLocationDeliveryCalendar',
        parameters: {
          serviceLocationId: serviceLocation.id,
          year: months[0] && months[0].format('YYYY'),
        },
        fullResponse: true,
      });

      openFile({ data });
    } catch (error) {
      const errorMessage = get(error, 'response.body.message', null);
      showSomethingWentWrong(errorMessage, '020');
    }

    changePdfFetching(false);
  };

  const handleDayClick = (day) => {
    const date = moment(day);
    const deliveryOrder = get(calendarDates, [
      date.format('YYYY-MM'),
      date.format('DD'),
    ]);
    if (
      deliveryOrder &&
      (deliveryOrder.skipDelivery ||
        (deliveryOrder.type !== 'hold' && deliveryOrder.type !== 'ignore'))
    ) {
      localStorage.removeItem('profilePage');
      localStorage.removeItem('nextDeliveryPage');
      loadDeliveryOrder(deliveryOrder);
    }
  };

  const loadDates = (newMonths) => {
    if (!serviceLocation || !newMonths?.length) return;

    const today = moment().format('YYYY-MM-DD');
    const isNewMonthThisMonth = newMonths[0].isSame(today, 'month');
    const startOfNewMonth = newMonths[0].startOf('month').format('YYYY-MM-DD');

    const fromDate = isNewMonthThisMonth ? today : startOfNewMonth;
    const toDate = newMonths.at(-1).endOf('month').format('YYYY-MM-DD');

    loadCalendarDates({
      serviceLocationId: serviceLocation.id,
      toDate,
      fromDate,
    });
  };

  useEffect(() => {
    loadDates(months);
  }, [serviceLocation?.id, months]);

  useEffect(() => {
    if (deliveryOrderToLoad) {
      const found = sortedOrders(calendarDates, isVertPhone).find(
        (cd) => cd.date === deliveryOrderToLoad.date,
      );
      if (found) {
        localStorage.removeItem('profilePage');
        localStorage.removeItem('nextDeliveryPage');
        loadDeliveryOrder(found);
        changeDeliveryOrderToLoad(null);
      }
    }
  }, [calendarDates]);

  const enableEdit = !(
    isFirstDelivery ||
    isSelectedServiceLocationExcluded ||
    isEquipmentPickupLockout ||
    isHardLockout
  );

  return (
    <Loader loading={isFetchingServiceLocation}>
      <div className={styles.wrapper}>
        {serviceLocation && serviceLocation.nextDeliveryDate ? (
          <Fragment>
            <Container value="Future Orders" className={styles.container}>
              <div className={styles.header}>
                <Span>Manage upcoming deliveries</Span>
                <HtmlData tracking={GTM_TRACKING_TAGS['Print Schedule']}>
                  <Button
                    text={`Print ${
                      months[0] && months[0].format('YYYY')
                    } Schedule`}
                    data-tracking={GTM_TRACKING_TAGS['Print Schedule']}
                    onClick={handlePdfChange}
                    loading={pdfFetching}
                    disabled={pdfFetching}
                  />
                </HtmlData>
              </div>
              <Div className={styles.subheader}>
                View or edit your delivery schedule and orders up to 90 days in
                advance.
              </Div>
              <div className={styles.calendarContainer}>
                <Calendar
                  narrowView={false}
                  calendarDates={calendarDates}
                  changeMonths={handleChangeMonths}
                  changeRange={handleChangeRange}
                  loading={isFetching}
                  onDayChange={handleDayClick}
                  disableForwardAfter={isMobile ? 2 : 1}
                  hideOutsideRange
                />
              </div>
              {renderLegend(months, calendarDates, shownMonths)}
              {renderOrders(calendarDates, isVertPhone, onEditDeliveryOrder)}
            </Container>
            <div className={styles.footer}>
              <Icon className={styles.icon} icon="cog" />
              <Div className={styles.text}>
                Need to add to or edit your recurring order?
              </Div>
              {enableEdit && (
                <Button
                  link
                  text="Edit Order"
                  data-tracking="Edit Recurring Order"
                  onClick={onEditRecurringOrder}
                  ariaLabel="Edit your recurring order"
                />
              )}
            </div>
          </Fragment>
        ) : (
          <div
            className={styles.noDataContainer}
            data-tracking={GTM_TRACKING_TAGS['No deliveries scheduled']}
          >
            <span>No deliveries scheduled yet</span>
            <span>
              Check out our refreshing selection of your favorite brands and add
              a few to your order.{' '}
            </span>
            <Button
              text="Browse Products"
              ariaLabel="Browse Products"
              onClick={handleBrowseProducts}
              disabled={!serviceLocation}
            />
          </div>
        )}
      </div>
      <OrderEditor />
      <EditDeliveryOrderDrawer
        onRefresh={handleRefresh}
        onRemoveHold={handleRemoveHold}
        onCancelEditSkipDelivery={handelCancelEditSkipDelivery}
      />
      <ProcessingModal />
    </Loader>
  );
};

FutureOrders.propTypes = {
  calendarDates: PropTypes.object.isRequired,
  loadCalendarDates: PropTypes.func.isRequired,
  showSomethingWentWrong: PropTypes.func.isRequired,
  loadRecurringOrder: PropTypes.func.isRequired,
  loadDeliveryOrder: PropTypes.func.isRequired,
  serviceLocation: PropTypes.object,
  isFetching: PropTypes.bool.isRequired,
  isFetchingServiceLocation: PropTypes.bool.isRequired,
  isHardLockout: PropTypes.bool.isRequired,
  isFirstDelivery: PropTypes.bool.isRequired,
  isEquipmentPickupLockout: PropTypes.bool.isRequired,
  isSelectedServiceLocationExcluded: PropTypes.bool.isRequired,
};

const mapStateToProps = createStructuredSelector({
  calendarDates: selectCalendarDates(),
  serviceLocation: selectSelectedServiceLocation(),
  isFetching: selectIsFetchingCalendarDates(),
  isFetchingServiceLocation: selectServiceLocationIsFetching(),
  isHardLockout: selectIsHardLockout(),
  isFirstDelivery: selectIsFirstDelivery(),
  isEquipmentPickupLockout: selectIsEquipmentPickupLockout(),
  isSelectedServiceLocationExcluded: selectIsSelectedServiceLocationExcluded(),
});

const mapDispatchToProps = {
  loadCalendarDates: loadCalendarDatesRequest,
  showSomethingWentWrong: showSomethingWentWrongAction,
  loadRecurringOrder: loadRecurringOrderRequest,
  loadDeliveryOrder: loadDeliveryOrderRequest,
  removeHold: removeHoldRequest,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FutureOrders),
);
